import { Spinner } from '@creditornot/cb-progress';
import React, { useMemo } from 'react';
import { Switch, useRouteMatch } from 'react-router-dom';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { ProductLine, Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { RouteWhenAuthenticated } from 'common/components';
import { EntityType } from 'common/types';
import {
  ExperienceVenueEntity,
  MessagePage,
  useCanViewCampaigns,
  useExperienceModuleContext,
} from 'modules/experience-core';
import { ExperienceEntityType } from 'modules/experience-core';
import { useSelectedEntityVenues } from 'modules/experience-core';

import { CampaignModuleContextProps, CampaignModuleContextProvider } from './context';
import useCampaignTypesPermittedToEdit from './hooks/useCampaignTypesPermittedToEdit';
import { DetailsCampaignPage } from './pages/DetailsCampaignPage';
import { EditCampaignPage } from './pages/EditCampaignPage';
import { ListCampaignsPage } from './pages/ListCampaignsPage/ListCampaignsPage';
import { NewCampaignPage } from './pages/NewCampaignPage/NewCampaignPage';

const ContextAwareCampaigns = ({
  entityType,
  merchantId,
  productLines,
  campaignTypesPermittedToEdit,
  venues,
  menuId,
}: {
  entityType: Extract<EntityType, 'venue' | 'merchant'>;
  merchantId: string;
  productLines: ProductLine[];
  venues: Venue[];
  campaignTypesPermittedToEdit: CampaignType[];
  menuId?: string;
}) => {
  const { path } = useRouteMatch();
  const { timezone, currency, country } = useExperienceModuleContext();
  const props = useMemo(
    () => ({
      merchantId,
      timezone,
      country,
      productLines,
      venueIds: venues.map(venue => venue.id),
      campaignTypesPermittedToEdit,
      menuId,
    }),
    [merchantId, timezone, country, venues, campaignTypesPermittedToEdit, menuId, productLines],
  );

  const contextValue: CampaignModuleContextProps = {
    currency,
    entityType,
    country,
    venues,
    merchantId,
    timezone,
    isExperienceFlow: true,
  };

  const venue = entityType === 'venue' ? venues[0] : undefined;
  const venueDetails = venue
    ? { name: venue.name, short_description: venue.short_description, listimage: venue.listimage }
    : undefined;

  return (
    <CampaignModuleContextProvider value={contextValue}>
      <Switch>
        <RouteWhenAuthenticated exact path={path}>
          <ListCampaignsPage {...props} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/new`}>
          <NewCampaignPage {...props} venueDetails={venueDetails} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/:campaignId`}>
          <DetailsCampaignPage {...props} venueDetails={venueDetails} />
        </RouteWhenAuthenticated>

        <RouteWhenAuthenticated exact path={`${path}/:campaignId/edit`}>
          <EditCampaignPage {...props} venueDetails={venueDetails} />
        </RouteWhenAuthenticated>
      </Switch>
    </CampaignModuleContextProvider>
  );
};

const ExperienceMerchantCampaigns = () => {
  const {
    country,
    selectedExperienceEntity: { experienceEntityId },
  } = useExperienceModuleContext();
  const experienceEntityVenues = useSelectedEntityVenues();
  const { campaignTypesPermittedToEdit, isLoading } = useCampaignTypesPermittedToEdit({
    feature: 'merchant',
    featureId: experienceEntityId,
    entityCountry: country,
  });

  const campaignProps = useMemo(
    () => ({
      entityType: 'merchant' as const,
      merchantId: experienceEntityId,
      productLines: [
        ...new Set(
          experienceEntityVenues
            .map(experienceEntityVenue => experienceEntityVenue.experienceEntityObject.product_line)
            .flat(),
        ),
      ],
      venues: experienceEntityVenues.map(venue => venue.experienceEntityObject),
      campaignTypesPermittedToEdit,
    }),
    [experienceEntityVenues, experienceEntityId, campaignTypesPermittedToEdit],
  );

  if (isLoading) {
    return <Spinner size="large" />;
  }

  return <ContextAwareCampaigns {...campaignProps} />;
};

const ExperienceVenueCampaigns = ({ experienceVenueEntity }: { experienceVenueEntity: ExperienceVenueEntity }) => {
  const { city, country, experienceEntity } = useExperienceModuleContext();
  const { campaignTypesPermittedToEdit, isLoading } = useCampaignTypesPermittedToEdit({
    feature: 'venue',
    featureId: experienceVenueEntity.experienceEntityId,
    city,
    entityCountry: country,
    productLine: experienceVenueEntity?.experienceEntityObject.product_line,
  });

  const canViewCampaigns = useCanViewCampaigns(experienceEntity, [experienceVenueEntity]);

  const campaignProps = useMemo(
    () => ({
      entityType: 'venue' as const,
      merchantId: experienceVenueEntity.experienceEntityObject.merchant,
      productLines: [experienceVenueEntity.experienceEntityObject.product_line],
      menuId: experienceVenueEntity?.experienceEntityObject.menu ?? undefined,
      venues: [experienceVenueEntity.experienceEntityObject],
      campaignTypesPermittedToEdit,
    }),
    [experienceVenueEntity, campaignTypesPermittedToEdit],
  );

  if (isLoading) {
    return <Spinner size="large" />;
  }

  return canViewCampaigns ? (
    <ContextAwareCampaigns {...campaignProps} />
  ) : (
    <MessagePage variant="missing_permissions" />
  );
};

export const ExperienceCampaigns = () => {
  const { experienceEntity, selectedExperienceEntity, selectedExperienceVenueEntity } = useExperienceModuleContext();
  const isMerchantSelectedInBothSelector =
    experienceEntity.experienceEntityType === ExperienceEntityType.MERCHANT &&
    selectedExperienceEntity.experienceEntityType === ExperienceEntityType.MERCHANT;
  if (isMerchantSelectedInBothSelector) {
    return <ExperienceMerchantCampaigns />;
  }

  if (selectedExperienceVenueEntity) {
    return (
      <ExperienceVenueCampaigns
        experienceVenueEntity={
          experienceEntity.experienceEntityType === ExperienceEntityType.MERCHANT &&
          selectedExperienceEntity.experienceEntityType === ExperienceEntityType.VENUE
            ? selectedExperienceEntity
            : selectedExperienceVenueEntity
        }
      />
    );
  }

  return null;
};
